import { AskPrompt } from "./AskPrompt";
import { forwardRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Copy, FileText, GitCompare, Minus, Plus, ShoppingCart, CheckCircle2 } from "lucide-react";
import { getLists, createList, addListItem } from "../../lib/cartApi";
import { isInCompare, toggleCompare } from "../../lib/compare";
import { CompareHelp } from "../CompareHelp";
import { DemoBadge } from "../DemoBadge";
import { ClearanceAltLink, SavingLine } from "../ClearanceBits";
import { useCustomer } from "../CustomerContext";
import { StockPill, stockInfo } from "../StockPill";
import { NotifyMe } from "./NotifyMe";

export const money = (v) => `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const canBuy = (p) => p.is_sellable && Number(p.price_inc_gst) > 0;  // $0 / no price fails closed
// T51b: available = stock on hand - reservations (server), stock_qty as a safety net, cart line ceiling 999
export const maxQty = (p) => Math.max(1, Math.min(p.available_qty ?? p.stock_qty ?? 999, 999));

export { stockInfo } from "../StockPill";  // T60: one stock pill / label source

const CopyCode = ({ label, value, id }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); toast.success(`${label} copied`); }
    catch { toast.error("Couldn't copy"); }
  };
  return (
    <span className="inline-flex items-center gap-1" data-testid={`product-${id}`}>
      <span className="text-slate-500">{label}:</span> <span className="font-mono text-slate-700">{value}</span>
      <button type="button" onClick={copy} aria-label={`Copy ${label} ${value}`} data-testid={`copy-${id}`}
        className="ml-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-[var(--brand-primary)]">
        {done ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
      </button>
    </span>
  );
};

export const strip = (h) => String(h || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();

export async function addToQuote(p, qty, nav) {
  try {  // T59: guests can START a quote (a "Quote request" list on this device); sign-in is asked for only when submitting
    const lists = await getLists();
    const arr = Array.isArray(lists) ? lists : lists?.lists || lists?.items || [];
    let l = arr.find((x) => x.name === "Quote request");
    if (!l) l = await createList("Quote request", "Items to send for a formal quote");
    await addListItem(l.id, p.id, qty);
    toast.success("Added to your quote list", { description: "Review the list and press Request quote.", action: { label: "Review", onClick: () => nav("/lists?start=quote") } });
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Couldn't add this item to a quote.");
  }
}

export const BuyBox = forwardRef(({ p, qty, setQty, onAdd, adding, highlights }, ref) => {
  const nav = useNavigate();
  const { customer } = useCustomer();
  const [inCompare, setInCompare] = useState(isInCompare(p.slug));
  const s = stockInfo(p);
  const buy = canBuy(p);
  return (
    <div className="lg:sticky lg:top-[96px]" data-testid="buy-box">
      <div className="flex min-h-[24px] flex-wrap items-center gap-x-3 gap-y-2" data-testid="pdp-brand-row">
        {p.brand && (p.brand.has_page ? <Link to={`/brands/${p.brand.slug}`} className="text-sm font-semibold uppercase tracking-wide text-[var(--brand-link)] hover:underline" data-testid="product-brand-link">{p.brand.display_name}</Link> : <span className="text-sm font-semibold uppercase tracking-wide text-slate-500" data-testid="product-brand-name">{p.brand.display_name}</span>)}
        <DemoBadge p={p} testid="pdp-demo-badge" />
      </div>
      <h1 className="rs-h1 mt-3 text-slate-900" data-testid="product-title">{p.display_name}</h1>
      <div className="mt-3 flex flex-wrap gap-2">
        {p.is_bundle && <span data-testid="product-bundle-badge" className="rounded-full border border-[var(--brand-primary)] px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[var(--brand-primary)]">Bundle</span>}
        {p.badge && <span data-testid="product-badge" className="rounded-full bg-[var(--brand-button)] px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[var(--brand-on-button)]">{p.badge}</span>}
      </div>
      {(p.sku || p.mpn) && (
        <div className="mt-2 flex flex-wrap gap-x-4 text-xs" data-testid="product-codes">
          {p.mpn && <CopyCode label="MPN" value={p.mpn} id="mpn" />}
          {p.sku && <CopyCode label="SKU" value={p.sku} id="sku" />}
        </div>
      )}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {p.purchase_state === "price_on_request" ? <div className="text-2xl font-extrabold text-[var(--brand-primary)]" data-testid="product-price-por">Price on Request</div> : Number(p.price_inc_gst) > 0 ? (
          <div data-testid="product-price">
            <div className="text-3xl font-extrabold tabular-nums text-[var(--brand-primary)]" data-testid="price-inc">{money(p.price_inc_gst)} <span className="text-sm font-semibold text-slate-500">inc GST</span></div>
            {p.clearance && <div className="mt-1"><SavingLine p={p.clearance} testid="pdp-saving" /></div>}
          </div>
        ) : <div className="text-lg font-semibold text-slate-600" data-testid="product-price-poa">Price on application</div>}
        <div className="mt-4">
          <StockPill p={p} />
          {s.note && <p className="mt-2 text-sm text-slate-600" data-testid={buy ? "product-stock-note" : "product-unavailable-note"}>{s.note}</p>}
          <ClearanceAltLink alt={p.clearance_alt} />
          {p.clearance?.new_unit && <Link to={`/p/${p.clearance.new_unit.slug}`} data-testid="pdp-clearance-new-link" className="mt-3 block text-sm font-semibold text-[var(--brand-link)] hover:underline">
            Prefer new? View the new unit - {money(p.clearance.new_unit.price_inc_gst)}</Link>}
        </div>
        <div ref={ref} className="mt-5 flex flex-wrap items-center gap-3" data-testid="product-add-controls">
          {buy && (
            <>
              <div className="flex items-center rounded-full border border-slate-300" role="group" aria-label="Quantity">
                <button type="button" data-testid="product-qty-dec" aria-label="Decrease quantity" onClick={() => setQty(Math.max(1, qty - 1))} className="flex h-11 w-11 items-center justify-center rounded-l-full hover:bg-slate-100"><Minus size={16} aria-hidden="true" /></button>
                <span data-testid="product-qty" className="w-10 text-center font-semibold tabular-nums" aria-live="polite">{qty}</span>
                <button type="button" data-testid="product-qty-inc" aria-label="Increase quantity" disabled={qty >= maxQty(p)}
                  onClick={() => setQty(Math.min(maxQty(p), qty + 1))} className="flex h-11 w-11 items-center justify-center rounded-r-full hover:bg-slate-100 disabled:opacity-40"><Plus size={16} aria-hidden="true" /></button>
              </div>
              <button type="button" data-testid="product-add-to-cart" disabled={adding} onClick={onAdd}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-[var(--brand-button)] px-6 font-bold text-[var(--brand-on-button)] transition-colors hover:bg-[var(--brand-button-hover)] active:bg-[var(--brand-button-active)] disabled:opacity-50">
                <ShoppingCart size={18} aria-hidden="true" /> {adding ? "Adding…" : "Add to cart"}
              </button>
            </>
          )}
          {["out_of_stock", "temporarily_unavailable"].includes(p.purchase_state) && <NotifyMe p={p} />}
          {p.availability_state !== "reserved" && (  // T49c: all units reserved -> no quantity, cart or quote
          <button type="button" data-testid="product-add-to-quote" onClick={() => addToQuote(p, qty, nav, customer)}
            className={`flex h-11 items-center justify-center gap-2 rounded-full border border-[var(--brand-primary)] px-5 text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:bg-slate-50 ${buy ? "w-full" : "flex-1"}`}>
            <FileText size={16} aria-hidden="true" /> {buy ? "Add to quote" : p.purchase_state === "availability_unconfirmed" ? "Enquire about availability" : p.purchase_state === "price_on_request" ? "Request a Quote" : "Request a quote"}
          </button>)}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <button type="button" data-testid="product-add-compare" aria-pressed={inCompare} data-state={inCompare ? "added" : "idle"} onClick={() => { toggleCompare(p); setInCompare(isInCompare(p.slug)); }}
            className={`inline-flex min-h-[44px] items-center gap-2 ${inCompare ? "font-semibold text-[var(--brand-primary)]" : "text-slate-600 hover:text-[var(--brand-primary)]"}`}>
            {inCompare ? <Check size={16} aria-hidden="true" /> : <GitCompare size={16} aria-hidden="true" />} {inCompare ? "Added to compare ✓" : "Add to compare"}
          </button>
          <CompareHelp testid="product-compare-help" className="-ml-3 h-11 w-11" />
          {buy && <button type="button" data-testid="product-view-cart" onClick={() => nav("/cart")} className="min-h-[44px] text-slate-600 underline hover:text-[var(--brand-primary)]">View cart</button>}
        </div>
      </div>
      {highlights.length > 0 && (
        <div className="mt-6" data-testid="product-highlights">
          <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">Key highlights</h2>
          <ul className="space-y-2">
            {highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[var(--brand-link)]" aria-hidden="true" /> {h}</li>
            ))}
          </ul>
        </div>
      )}
      <AskPrompt p={p} />
    </div>
  );
});
